/**
 * Parses a JWT token and checks its expiration status.
 *
 * @param {string | null | undefined} token - The JWT token to be parsed and checked.
 * @returns {Object} An object containing:
 *   - `tokenParsed` {Object|undefined}: The parsed token payload, or `undefined` if the token is invalid.
 *   - `tokenIsExpired` {boolean}: A boolean indicating whether the token is expired.
 *   - `timeUntilExpiry` {number|undefined}: The time in minutes until the token expires, or `undefined` if the token is expired or invalid.
 */
export const parseAndCheckToken = (
	token: string | null | undefined,
): { tokenIsExpired: boolean; timeUntilExpiry: number; repId: string | null } => {
	let tokenParsed
	try {
		tokenParsed = token
			? JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
			: undefined
	} catch (e) {
		console.error('Failed to parse token:', e)
		return {
			tokenIsExpired: true,
			timeUntilExpiry: 0,
			repId: null,
		}
	}

	const dateNowInSeconds = Math.floor(Date.now() / 1000)
	const tokenIsExpired = tokenParsed?.exp
		? dateNowInSeconds > tokenParsed.exp
		: false

	let timeUntilExpiry = 0

	if (tokenIsExpired === false) {
		timeUntilExpiry = (tokenParsed.exp - dateNowInSeconds) / 60
	}

	return { tokenIsExpired, timeUntilExpiry, repId: tokenParsed?.repId ?? null }
}

/**
 * Checks if a token is expired based on its expiry time.
 *
 * @param tokenExpiryTime - The expiry time of the token in seconds since the Unix epoch.
 * @param currentTime - Optional. Input the user's current time in seconds. If not provided, the current system time will be used.
 * @returns `true` if the token is expired, `false` otherwise.
 */
export const isTokenExpired = (
	tokenExpiryTime: number | string,
	currentTime?: number | string,
	timeZone?: string,
): boolean => {
	const userTimeinSeconds =
		timeZone && currentTime
			? Math.floor(
					new Date(
						new Intl.DateTimeFormat('en-US', { timeZone }).format(
							Number(currentTime) * 1000,
						),
					).getTime() / 1000,
				)
			: Math.floor(Date.now() / 1000)
	const dateNowInSeconds = Math.floor(Date.now() / 1000)
	let isExpired = true

	// console.log('tokenExpiryTime', tokenExpiryTime)
	// console.log('currentTime', currentTime)
	// console.log('dateNowInSeconds', dateNowInSeconds)

	if (currentTime) {
		isExpired = Number(userTimeinSeconds) > Number(tokenExpiryTime)
	} else {
		isExpired = dateNowInSeconds > Number(tokenExpiryTime)
	}

	return isExpired
}
